import { getApiErrorCode, getApiErrorStatus } from "@/lib/api-error";
import { EmployeeDTO } from "@shaarp/common";

/** Title/description pair for the toast a failed reveal deserves. */
export type EmailRevealErrorMessages = {
  title: string;
  description: string;
};

const EMAIL_REVEAL_ERROR_KEYS = {
  limitReached: {
    title: "components.dataTableCompanyEmployees.email.limitReachedError.title",
    description:
      "components.dataTableCompanyEmployees.email.limitReachedError.description",
  },
  unavailable: {
    title:
      "components.dataTableCompanyEmployees.email.revealUnavailableError.title",
    description:
      "components.dataTableCompanyEmployees.email.revealUnavailableError.description",
  },
} as const satisfies Record<string, EmailRevealErrorMessages>;

/**
 * Translation keys for a reveal the API refused, or null when the failure is
 * not one the endpoint codes — callers then fall back to the generic toast
 * rather than inventing an explanation. A throttle (429) is not handled here:
 * the shared rate-limit warning (`useRateLimitWarning`) quotes the limit.
 *
 * A 403 has several server-side causes, so the body's `ContactRevealErrorCode`
 * decides between the trial cap and the team simply not being allowed to
 * reveal.
 *
 * @param error The rejected request.
 */
export const getEmailRevealErrorMessages = (
  error: unknown,
): EmailRevealErrorMessages | null => {
  const status = getApiErrorStatus(error);
  if (status === 403) {
    return getApiErrorCode(error) === "EMAIL_LIMIT_REACHED"
      ? EMAIL_REVEAL_ERROR_KEYS.limitReached
      : EMAIL_REVEAL_ERROR_KEYS.unavailable;
  }
  return null;
};

/**
 * Whether asking the API for this contact's email can still produce one.
 *
 * A contact whose team already got a verdict of "no address exists", or one
 * banned from the platform, would cost a round-trip to be told the same thing
 * — the caller reports the missing address straight away instead.
 *
 * @param employee The contact about to be revealed.
 */
export const canRevealEmployeeEmail = (employee: EmployeeDTO): boolean =>
  !employee.isBlacklisted && employee.emailStatus !== "UNAVAILABLE";
